import { useEffect, useState, useCallback } from "react";
import { Plus, Building2, KeyRound, Trash2, ArrowRight, UserCog, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg, fmt } from "@/lib/api";
import { useInvalidate } from "@/hooks/useDeviceLive";
import ConfirmDialog from "@/components/common/ConfirmDialog";

// Small enough to store inline on the tenant doc as a data: URI — no file
// storage backend exists, and a profile picture doesn't need to be large.
const MAX_LOGO_BYTES = 400 * 1024;

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const Field = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="kl-label">{label}</span>
    <input data-testid={testid} className="kl-input-box mt-1" {...p} />
  </label>
);

function CreateTenantDialog({ open, onOpenChange, onCreated }) {
  const [f, setF] = useState({ name: "", admin_name: "", admin_email: "", admin_password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tenants", f);
      toast.success("Dealer created", { description: `${data.name} — admin ${data.admin_email}` });
      setF({ name: "", admin_name: "", admin_email: "", admin_password: "" });
      onCreated();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-none border-kl-black p-0 gap-0" data-testid="create-tenant-dialog">
        <DialogHeader className="bg-kl-black text-white p-6">
          <p className="kl-label-dark">New dealer</p>
          <DialogTitle className="font-display font-light text-2xl">Create a dealer</DialogTitle>
          <DialogDescription className="text-kl-muted">It gets its own regions and default catalog. The email + password below become that dealer's login for the web app and mobile app.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Dealer name" testid="tenant-name-input" required value={f.name} onChange={set("name")} placeholder="Acme Solar" />
          <Field label="Admin display name (optional)" testid="tenant-admin-name-input" value={f.admin_name} onChange={set("admin_name")} placeholder="Acme Solar Operator" />
          <Field label="Admin login email" testid="tenant-admin-email-input" type="email" required value={f.admin_email} onChange={set("admin_email")} placeholder="ops@acmesolar.com" />
          <Field label="Admin password" testid="tenant-admin-password-input" type="text" required minLength={6} value={f.admin_password} onChange={set("admin_password")} placeholder="at least 6 characters" />
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => onOpenChange(false)} className="kl-btn-outline" data-testid="tenant-cancel-button">Cancel</button>
            <button disabled={busy} className="kl-btn-primary" data-testid="tenant-submit-button">{busy ? "Creating…" : "Create dealer"} <ArrowRight size={15} /></button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ tenant, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/tenants/${tenant.id}`, { admin_password: pw });
      toast.success("Admin password updated", { description: `${tenant.name} — ${tenant.admin_email}` });
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none border-kl-black p-0 gap-0" data-testid="tenant-password-dialog">
        <DialogHeader className="bg-kl-black text-white p-6">
          <p className="kl-label-dark">{tenant.name}</p>
          <DialogTitle className="font-display font-light text-2xl">Set admin password</DialogTitle>
          <DialogDescription className="text-kl-muted">New password for {tenant.admin_email}. They'll need it for their next login.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="New password" testid="tenant-newpw-input" type="text" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="at least 6 characters" />
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="kl-btn-outline">Cancel</button>
            <button disabled={busy} className="kl-btn-primary" data-testid="tenant-newpw-submit">{busy ? "Saving…" : "Update password"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDialog({ tenant, onClose, onSaved }) {
  const [name, setName] = useState(tenant.name);
  const [gst, setGst] = useState(tenant.gst_number || "");
  const [logo, setLogo] = useState(tenant.logo_url || "");
  const [busy, setBusy] = useState(false);

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Pick an image file");
    if (file.size > MAX_LOGO_BYTES) return toast.error("Image is too large — please use one under 400 KB");
    try { setLogo(await readFileAsDataUrl(file)); } catch { toast.error("Could not read that image"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name, gst_number: gst };
      if (!logo && tenant.logo_url) body.clear_logo = true;
      else if (logo !== tenant.logo_url) body.logo_url = logo;
      await api.patch(`/admin/tenants/${tenant.id}`, body);
      toast.success("Dealer profile updated");
      onSaved();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-none border-kl-black p-0 gap-0" data-testid="tenant-profile-dialog">
        <DialogHeader className="bg-kl-black text-white p-6">
          <p className="kl-label-dark">Dealer profile</p>
          <DialogTitle className="font-display font-light text-2xl">{tenant.name}</DialogTitle>
          <DialogDescription className="text-kl-muted">Agency details shown alongside this dealer across the platform.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 border border-kl-lineLight bg-kl-light shrink-0 flex items-center justify-center overflow-hidden">
              {logo ? <img src={logo} alt="" className="w-full h-full object-cover" /> : <Building2 size={22} className="text-kl-mutedLight" />}
            </div>
            <div className="flex-1 space-y-1">
              <span className="kl-label block">Profile picture (optional)</span>
              <div className="flex gap-2">
                <label className="kl-btn-outline h-9 px-3 text-xs cursor-pointer" data-testid="tenant-logo-upload-label">
                  <ImagePlus size={13} /> {logo ? "Replace" : "Upload"}
                  <input type="file" accept="image/*" onChange={pickLogo} className="hidden" data-testid="tenant-logo-input" />
                </label>
                {logo && <button type="button" onClick={() => setLogo("")} className="kl-btn-ghost h-9 px-2 text-xs" data-testid="tenant-logo-remove"><X size={13} /> Remove</button>}
              </div>
            </div>
          </div>
          <Field label="Agency name" testid="tenant-profile-name-input" required value={name} onChange={(e) => setName(e.target.value)} />
          <Field label="GST number (optional)" testid="tenant-profile-gst-input" value={gst} onChange={(e) => setGst(e.target.value)} placeholder="22AAAAA0000A1Z5" />
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="kl-btn-outline">Cancel</button>
            <button disabled={busy} className="kl-btn-primary" data-testid="tenant-profile-submit">{busy ? "Saving…" : "Save profile"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Tenants() {
  const [tenants, setTenants] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [pwTenant, setPwTenant] = useState(null);
  const [profileTenant, setProfileTenant] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => api.get("/admin/tenants").then((r) => setTenants(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  useInvalidate(["tenants", "devices"], load, { admin: true });

  const remove = async () => {
    try {
      await api.delete(`/admin/tenants/${removing.id}`);
      toast.success("Dealer deleted", { description: `${removing.name} and all its data were removed.` });
      setRemoving(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="h-full overflow-y-auto kl-scroll-light p-8" data-testid="tenants-page">
      <div className="flex items-end justify-between gap-6 pb-6 border-b border-kl-black">
        <div>
          <p className="kl-label">Platform</p>
          <h1 className="font-display font-light text-3xl mt-1">Dealers <span className="font-mono text-sm text-kl-mutedLight">{tenants.length}</span></h1>
          <p className="text-sm text-kl-mutedLight mt-1">Each dealer is isolated — own devices, catalog, reports and history. You create their login and can reset it.</p>
        </div>
        <button onClick={() => setCreateOpen(true)} className="kl-btn-primary shrink-0" data-testid="add-tenant-button"><Plus size={15} /> New dealer</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-px bg-kl-lineLight border border-kl-lineLight mt-6">
        {tenants.map((t) => (
          <div key={t.id} className="bg-white p-5 group" data-testid={`tenant-card-${t.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex items-start gap-3">
                {t.logo_url ? (
                  <img src={t.logo_url} alt="" className="w-9 h-9 border border-kl-lineLight object-cover shrink-0" data-testid={`tenant-logo-${t.id}`} />
                ) : (
                  <div className="w-9 h-9 border border-kl-lineLight bg-kl-light flex items-center justify-center shrink-0"><Building2 size={15} className="text-kl-mutedLight" /></div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg truncate">{t.name}</h3>
                    {t.is_default && <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-kl-lineLight text-kl-mutedLight">default</span>}
                  </div>
                  <p className="text-xs text-kl-mutedLight mt-1 truncate">{t.admin_email || "no admin account"}</p>
                  {t.gst_number && <p className="text-[11px] text-kl-mutedLight mt-0.5 font-mono truncate">GST {t.gst_number}</p>}
                </div>
              </div>
              <div className="flex opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => setProfileTenant(t)} data-testid={`tenant-profile-${t.id}`} title="Edit profile" className="p-2 hover:bg-kl-black hover:text-white transition-colors"><UserCog size={14} /></button>
                <button onClick={() => setPwTenant(t)} data-testid={`tenant-pw-${t.id}`} title="Set admin password" className="p-2 hover:bg-kl-black hover:text-white transition-colors"><KeyRound size={14} /></button>
                {!t.is_default && <button onClick={() => setRemoving(t)} data-testid={`tenant-delete-${t.id}`} title="Delete dealer" className="p-2 hover:bg-kl-inactive hover:text-white transition-colors"><Trash2 size={14} /></button>}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-px bg-kl-lineLight border border-kl-lineLight mt-4 text-center">
              {[["Devices", t.device_count], ["Active", t.active_count], ["Reports", t.report_count], ["History", t.history_count]].map(([l, v]) => (
                <div key={l} className="bg-white py-2"><div className="kl-label">{l}</div><div className="font-mono text-sm mt-0.5">{v}</div></div>
              ))}
            </div>
            <p className="text-[11px] text-kl-mutedLight mt-3">Created {fmt.date(t.created_at)}</p>
          </div>
        ))}
      </div>

      <CreateTenantDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      {pwTenant && <PasswordDialog tenant={pwTenant} onClose={() => setPwTenant(null)} onSaved={load} />}
      {profileTenant && <ProfileDialog tenant={profileTenant} onClose={() => setProfileTenant(null)} onSaved={load} />}
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Delete this dealer?"
        description={removing ? `${removing.name} — its ${removing.device_count} devices, catalog, ${removing.report_count} reports and ${removing.history_count} history entries, and the admin login (${removing.admin_email}) will be permanently deleted.` : ""}
        confirmLabel="Delete dealer"
        onConfirm={remove}
      />
    </div>
  );
}
